import {sortBy, map, defaults} from 'lodash'
import {createSelector} from 'reselect'

import {ErrorPageShownImperativelyType} from './errorPages/consts'

export type PageProps = {
  hideTopbar?: boolean
  lockBodyScroll?: boolean
  callWidgetVisible?: boolean
  disableAutoRefresh?: boolean
  title?: string
  section?: string
}

export type PageInfo = {
  // unique mount id
  mountId: string
  // page id, or void
  pageId?: string
  // ids of all parent pages, separated by '/'
  pageParents: string
  timestamp: number
  priority: number
  pageProps: Partial<PageProps>
}

export type NavigationState = {
  location: {
    pathname: string | undefined | null
    search: string | undefined | null
    hash: string | undefined | null
  }
  loading: boolean
  progress: number
  controller?: string | null
  method?: string | null
  refreshOnNavigation: string | undefined | null
  mountedPages: ReadonlyArray<PageInfo>
  errorPageShownImperatively: ErrorPageShownImperativelyType
}

export type GlobalState = {
  navigation: NavigationState
}

export const defaultNavigationState: NavigationState = {
  location: {
    pathname: null,
    search: null,
    hash: null,
  },
  loading: false,
  progress: 0,
  controller: null,
  method: null,
  refreshOnNavigation: null,
  mountedPages: [],
  errorPageShownImperatively: null,
}

export const NAVIGATION_STORE_KEY = 'navigation'

const controllerSelector = ({navigation}) => navigation && navigation.controller
const methodSelector = ({navigation}) => navigation && navigation.method

export const navigationSelector = ({
  navigation = defaultNavigationState,
}: GlobalState): NavigationState => navigation

export const progressSelector = (state: GlobalState) => {
  return {
    progress: navigationSelector(state).progress,
  }
}
export const locationSelector = (state: GlobalState) => {
  return navigationSelector(state).location
}
export const isLoadingSelector = (state: GlobalState) =>
  navigationSelector(state).loading

export const controllerMethodSelector = createSelector(
  controllerSelector,
  methodSelector,
  (controller, method) => {
    return {
      controller,
      method,
    }
  }
)

export const mountedPagesSelector = createSelector(
  // @ts-ignore Ignored during migration
  (state) => navigationSelector(state).mountedPages,
  (_stack: ReadonlyArray<PageInfo>) => {
    let stack = sortBy(
      _stack,
      (page) => `${page.pageParents || ''}/${page.pageId || ''}/${page.mountId}`
    )
    const mountedParents = {}
    stack = stack.filter((page) => {
      if (page.pageParents && !mountedParents[page.pageParents]) {
        return false
      }
      if (page.pageId) {
        mountedParents[
          page.pageParents ? `${page.pageParents}/${page.pageId}` : page.pageId
        ] = true
      }
      return true
    })
    return sortBy(stack, ({priority = 0}) => priority)
  }
)

export const mountedPagePathsSelector = (state: GlobalState) =>
  mountedPagesSelector(state)
    .filter(({pageId}) => pageId != null)
    .map(({pageId, pageParents}) =>
      pageParents ? `${pageParents}/${pageId}` : pageId
    )

export const pagePropsSelector = createSelector(
  mountedPagesSelector,
  (pages: Array<PageInfo>) => {
    return defaults({}, ...map(pages, 'pageProps').reverse())
  }
)

export const refreshOnNavigationSelector = (state: GlobalState) =>
  navigationSelector(state).refreshOnNavigation

export const autoRefreshDisabledSelector = (state: GlobalState) =>
  pagePropsSelector(state).disableAutoRefresh === false

export const showErrorPageSelector = (state: GlobalState) =>
  navigationSelector(state).errorPageShownImperatively
